// apps/web/src/components/shell/paleta-comandos.tsx — Fase 17.8, Plano 15 (D-18, D-25; UI-SPEC U-13; T-17.8-15-01..04).
// Paleta Ctrl+K: sem texto mostra Recentes (cookie por usuário, só rotas ainda permitidas) e Ir para (ações que só navegam);
// com texto mostra as Telas permitidas (no cliente, na hora) e, com termo válido, os registros do servidor depois de 200 ms de
// debounce (GET /api/v1/busca, cancelado a cada digitação). Selecionar navega pela navegação client-side do app e fecha.
// A autorização real continua no servidor e em cada rota; aqui só se evita mostrar o que a sessão não abre.
import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { AlertCircle, ArrowRight, Boxes, Clock, Factory, FileText, Plus, Users, X } from 'lucide-react';
import type { BuscaGlobalResponse, ItemBusca } from '@pieltec/contracts';
import {
  Command, CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList, CommandLoading,
} from '../ui/command';
import { Skeleton } from '../ui/skeleton';
import {
  acoesDeNavegacao, carregarBusca, recentesVisiveis, rotuloDaRota, telasPermitidas, termoValido, type TelaPaleta,
} from '../../lib/busca/paleta';
import { lerRecentes, caminhoValido } from '../../lib/prefs-ui';
import '../../styles/paleta-comandos.css';

export interface PaletaComandosProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly permissoes: readonly string[];
  readonly userId: string;
  /** Devolve o foco ao elemento que o tinha antes de a paleta abrir (guardado pelo AppShell). */
  readonly aoDevolverFoco?: () => void;
}

/** Atraso entre a última tecla e a leitura no servidor (o limite do servidor é 60 pedidos por minuto). */
export const DEBOUNCE_BUSCA_MS = 200;

type EstadoBusca =
  | { readonly tipo: 'ocioso' }
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'erro' }
  | { readonly tipo: 'sucesso'; readonly dados: BuscaGlobalResponse };

const GRUPOS_DO_SERVIDOR = [
  { chave: 'pedidosVenda', titulo: 'Pedidos', tipo: 'pv', Icone: FileText },
  { chave: 'ordensProducao', titulo: 'Ordens de produção', tipo: 'op', Icone: Factory },
  { chave: 'lotes', titulo: 'Lotes', tipo: 'lote', Icone: Boxes },
  { chave: 'clientes', titulo: 'Clientes', tipo: 'cliente', Icone: Users },
] as const;

/** Navegação client-side do app (o router escuta `popstate`), sem recarregar; só caminho interno do mesmo domínio. */
export function navegarNoApp(destino: string): void {
  if (!caminhoValido(destino)) return;
  const url = new URL(destino, window.location.origin);
  if (url.origin !== window.location.origin) return;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return;
  window.history.pushState(null, '', `${url.pathname}${url.search}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

const idDeTeste = (texto: string): string => texto.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '');

function Linha({ icone, titulo, apoio, valor, testId, aoSelecionar }: {
  readonly icone: ReactNode;
  readonly titulo: string;
  readonly apoio?: string | undefined;
  readonly valor: string;
  readonly testId: string;
  readonly aoSelecionar: () => void;
}): ReactElement {
  return (
    <CommandItem value={valor} data-testid={testId} className="paleta-item" onSelect={aoSelecionar}>
      <span className="paleta-item__icone" aria-hidden="true">{icone}</span>
      <span className="paleta-item__titulo">{titulo}</span>
      {apoio !== undefined && <span className="paleta-item__apoio">{apoio}</span>}
      <kbd className="paleta-item__enter" aria-hidden="true">↵</kbd>
    </CommandItem>
  );
}

function ConteudoPaleta({ permissoes, userId, aoNavegar }: {
  readonly permissoes: readonly string[];
  readonly userId: string;
  readonly aoNavegar: (destino: string) => void;
}): ReactElement {
  const [valor, setValor] = useState('');
  const [busca, setBusca] = useState<EstadoBusca>({ tipo: 'ocioso' });
  const campo = useRef<HTMLInputElement>(null);
  const termo = valor.trim();
  const termoDoServidor = termoValido(termo) ? termo : null;

  // Montada só com a paleta aberta: o cookie é lido a cada abertura e o texto some ao fechar.
  const recentes = useMemo(() => recentesVisiveis(lerRecentes(document.cookie, userId), permissoes), [userId, permissoes]);
  const acoes = useMemo(() => acoesDeNavegacao(permissoes), [permissoes]);
  const telas = useMemo(() => telasPermitidas(permissoes, termo), [permissoes, termo]);

  // Debounce de 200 ms; cada nova digitação cancela o timer e a requisição anterior (AbortController).
  useEffect(() => {
    if (termoDoServidor === null) {
      setBusca({ tipo: 'ocioso' });
      return undefined;
    }
    setBusca({ tipo: 'carregando' });
    const controle = new AbortController();
    const timer = window.setTimeout(() => {
      void carregarBusca(termoDoServidor, controle.signal).then((resultado) => {
        if (controle.signal.aborted) return;
        setBusca(resultado.tipo === 'sucesso' ? { tipo: 'sucesso', dados: resultado.dados } : { tipo: 'erro' });
      });
    }, DEBOUNCE_BUSCA_MS);
    return () => {
      window.clearTimeout(timer);
      controle.abort();
    };
  }, [termoDoServidor]);

  const gruposDoServidor = busca.tipo === 'sucesso'
    ? GRUPOS_DO_SERVIDOR.map((g) => ({ ...g, itens: busca.dados[g.chave] ?? ([] as readonly ItemBusca[]) })).filter((g) => g.itens.length > 0)
    : [];
  const semResultado = termo !== '' && telas.length === 0 && busca.tipo !== 'carregando'
    && (busca.tipo === 'ocioso' || (busca.tipo === 'sucesso' && gruposDoServidor.length === 0));

  const linhaDeTela = (tela: TelaPaleta, tipo: string, apoio: string | undefined): ReactElement => (
    <Linha
      key={`${tipo}:${tela.id}`} valor={`${tipo}:${tela.id}`} testId={`paleta-item-${tipo}-${tela.id}`}
      icone={tipo === 'acao' ? <Plus className="size-4" /> : <ArrowRight className="size-4" />}
      titulo={tela.rotulo} apoio={apoio} aoSelecionar={() => aoNavegar(tela.caminho)}
    />
  );

  return (
    <Command shouldFilter={false} vimBindings={false} loop label="Busca e navegação" className="paleta">
      <CommandInput
        ref={campo} value={valor} onValueChange={setValor}
        placeholder="Buscar telas, pedidos, ordens, lotes ou clientes…" data-testid="paleta-campo"
        acaoFinal={valor !== '' ? (
          <button
            type="button" className="paleta-limpar" aria-label="Limpar busca" data-testid="paleta-limpar"
            onClick={() => { setValor(''); campo.current?.focus(); }}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : undefined}
      />
      <CommandList className="paleta-lista">
        {termo === '' ? (
          <>
            {recentes.length > 0 ? (
              <CommandGroup heading="Recentes">
                {recentes.map((caminho) => (
                  <Linha
                    key={caminho} valor={`recente:${caminho}`} testId={`paleta-item-recente-${idDeTeste(caminho)}`}
                    icone={<Clock className="size-4" />} titulo={rotuloDaRota(caminho)} aoSelecionar={() => aoNavegar(caminho)}
                  />
                ))}
              </CommandGroup>
            ) : (
              <p className="paleta-estado" data-testid="paleta-sem-recentes">Nenhuma página recente. Comece digitando acima.</p>
            )}
            {acoes.length > 0 && (
              <CommandGroup heading="Ir para">
                {acoes.map((acao) => linhaDeTela(acao, 'acao', 'Abre a tela'))}
              </CommandGroup>
            )}
          </>
        ) : (
          <>
            {telas.length > 0 && (
              <CommandGroup heading="Telas">
                {telas.map((tela) => linhaDeTela(tela, 'tela', tela.grupo))}
              </CommandGroup>
            )}
            {gruposDoServidor.map(({ chave, titulo, tipo, Icone, itens }) => (
              <CommandGroup key={chave} heading={titulo}>
                {itens.map((item) => (
                  <Linha
                    key={item.id} valor={`${tipo}:${item.id}`} testId={`paleta-item-${tipo}-${item.id}`}
                    icone={<Icone className="size-4" />} titulo={item.titulo} apoio={item.secundario}
                    aoSelecionar={() => aoNavegar(item.href)}
                  />
                ))}
              </CommandGroup>
            ))}
            {busca.tipo === 'carregando' && (
              <CommandLoading label="Buscando registros">
                <div className="paleta-esqueleto" data-testid="paleta-esqueleto" aria-hidden="true">
                  <Skeleton className="paleta-esqueleto__linha" />
                  <Skeleton className="paleta-esqueleto__linha" />
                  <Skeleton className="paleta-esqueleto__linha" />
                </div>
              </CommandLoading>
            )}
            {busca.tipo === 'erro' && (
              <p className="paleta-estado paleta-estado--erro" role="status" data-testid="paleta-erro">
                <AlertCircle className="size-4" aria-hidden="true" />
                Não foi possível buscar registros agora. As telas continuam disponíveis.
              </p>
            )}
            {semResultado && (
              <p className="paleta-estado" role="status" data-testid="paleta-vazio">
                Nenhum resultado para “{termo}”. Tente o número do PV, da OP ou o nome do cliente.
              </p>
            )}
          </>
        )}
      </CommandList>
      <div className="paleta-rodape" aria-hidden="true">↑↓ navegar · ↵ abrir · esc fechar</div>
    </Command>
  );
}

export function PaletaComandos({ aberto, onAbertoChange, permissoes, userId, aoDevolverFoco }: PaletaComandosProps): ReactElement {
  const navegou = useRef(false);

  const navegar = (destino: string): void => {
    navegou.current = true;
    onAbertoChange(false);
    navegarNoApp(destino);
  };

  return (
    <CommandDialog
      open={aberto} onOpenChange={onAbertoChange} title="Busca e navegação"
      description="Digite para buscar telas, pedidos, ordens, lotes e clientes. Use as setas para escolher e Enter para abrir."
      className="paleta-caixa" overlayClassName="paleta-scrim" data-testid="paleta-comandos"
      onCloseAutoFocus={(evento) => {
        // Sem gatilho próprio (abre por atalho): o foco volta ao elemento anterior; depois de navegar, vai ao conteúdo novo.
        evento.preventDefault();
        if (navegou.current) {
          navegou.current = false;
          document.getElementById('main-content')?.focus();
        } else {
          aoDevolverFoco?.();
        }
      }}
    >
      {aberto && <ConteudoPaleta permissoes={permissoes} userId={userId} aoNavegar={navegar} />}
    </CommandDialog>
  );
}
